import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Moon } from 'lucide-react';
import { createEntry, updateEntry } from './entriesApi';

/**
 * Day 9 — Sleep chart entry form modal. Works for create + edit.
 * Validation rules mirror backend: sleep_start + sleep_end required,
 * sleep_end strictly after sleep_start, night_awakenings >= 0.
 */

const SLEEP_QUALITY_OPTIONS = [
  { value: '', label: '— Not recorded —' },
  { value: 'good', label: 'Good' },
  { value: 'disturbed', label: 'Disturbed' },
  { value: 'poor', label: 'Poor' },
];

function toLocalInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    // yyyy-MM-ddTHH:mm
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function fromLocalInput(local) {
  if (!local) return '';
  try { return new Date(local).toISOString(); } catch { return ''; }
}

export function SleepChartEntryFormModal({
  open, onOpenChange, participantId, chartId, entry = null, onSaved,
}) {
  const isEdit = !!entry?.id;
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [quality, setQuality] = useState('');
  const [awakenings, setAwakenings] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStart(toLocalInput(entry?.sleep_start));
    setEnd(toLocalInput(entry?.sleep_end));
    setQuality(entry?.sleep_quality || '');
    setAwakenings(entry?.night_awakenings != null ? String(entry.night_awakenings) : '');
    setNotes(entry?.notes || '');
  }, [open, entry]);

  const totalHoursPreview = (() => {
    if (!start || !end) return null;
    try {
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      if (!isFinite(s) || !isFinite(e) || e <= s) return null;
      return ((e - s) / 3600000).toFixed(2);
    } catch { return null; }
  })();

  const submit = async () => {
    if (!start) return toast.error('Sleep start is required');
    if (!end) return toast.error('Sleep end is required');
    if (new Date(end) <= new Date(start)) return toast.error('Sleep end must be after sleep start');
    if (awakenings !== '' && (Number.isNaN(Number(awakenings)) || Number(awakenings) < 0)) {
      return toast.error('Night awakenings must be 0 or more');
    }
    const payload = {
      sleep_start: fromLocalInput(start),
      sleep_end: fromLocalInput(end),
      sleep_quality: (quality && quality !== '__none__') ? quality : null,
      night_awakenings: awakenings === '' ? null : Number(awakenings),
      notes: notes || null,
    };
    try {
      setBusy(true);
      if (isEdit) await updateEntry(participantId, chartId, entry.id, payload);
      else await createEntry(participantId, chartId, payload);
      toast.success(isEdit ? 'Sleep entry updated' : 'Sleep entry saved');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to save sleep entry');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sleep-entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <Moon className="w-4 h-4 text-indigo-600" />
            {isEdit ? 'Edit sleep entry' : 'Record sleep'}
          </DialogTitle>
          <DialogDescription>
            Structured sleep monitoring. Total hours is calculated automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="sleep-start" className="text-xs">Sleep start *</Label>
              <Input id="sleep-start" type="datetime-local" value={start} onChange={e => setStart(e.target.value)} className="mt-1" data-testid="sleep-start-input" />
            </div>
            <div>
              <Label htmlFor="sleep-end" className="text-xs">Sleep end *</Label>
              <Input id="sleep-end" type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} className="mt-1" data-testid="sleep-end-input" />
            </div>
          </div>
          {totalHoursPreview && (
            <div className="text-[11px] text-muted-foreground" data-testid="sleep-total-preview">
              Total: <span className="font-medium text-foreground">{totalHoursPreview} hours</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="sleep-quality" className="text-xs">Sleep quality</Label>
              <Select value={quality} onValueChange={setQuality}>
                <SelectTrigger id="sleep-quality" className="mt-1" data-testid="sleep-quality-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {SLEEP_QUALITY_OPTIONS.map(o => (
                    <SelectItem key={o.value || 'none'} value={o.value || '__none__'}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sleep-awakenings" className="text-xs">Night awakenings</Label>
              <Input
                id="sleep-awakenings" type="number" min="0" value={awakenings}
                onChange={e => setAwakenings(e.target.value)} className="mt-1"
                data-testid="sleep-awakenings-input"
                placeholder="0"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="sleep-notes" className="text-xs">Notes</Label>
            <Textarea
              id="sleep-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={2000}
              placeholder="Optional — context for clinicians"
              className="mt-1" data-testid="sleep-notes-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1 text-right">{notes.length}/2000</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sleep-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="sleep-submit-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Save entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SleepChartEntryFormModal;
